import { useState, useEffect } from 'react';
import axios, { CancelToken } from 'axios';
import * as Sentry from '@sentry/browser';

import { STATUS } from '../constants';

const { IDLE, PENDING, SUCCESS, ERROR } = STATUS;

export function useAssetImages({ assetId, campsiteId, tourId, timedEntryTourId }) {
    const initialState = {
        heroImage: {},
        galleryImages: [],
    };

    const [imagesObject, setImagesObject] = useState(initialState);
    const [status, setStatus] = useState(IDLE);

    useEffect(() => {
        let isMounted = true;
        const cancelTokenSource = CancelToken.source();
        // get the content type needed for the api URL
        let contentType;
        let id;
        const galleryItems = [];
        const heroItems = [];
        const previewItems = [];

        if (tourId) {
            contentType = 'tour';
            id = tourId;
        }else if (timedEntryTourId) {
            contentType = 'timedentry_tour';
            id = timedEntryTourId;
        } else if (campsiteId) {
            contentType = 'campsite';
            id = campsiteId;
        }else if (assetId) {
            contentType = 'asset';
            id = assetId;
        }

        // Get images from api
        if (contentType && id) {
            setStatus(PENDING);

            const loadData = async () => {
                try {
                    const { data } = await axios.get(
                        `/api/media/public/${contentType}/${id}`,
                        {
                            cancelToken: cancelTokenSource.token,
                        }
                    );
                    if (!data || !!data.error) {
                        setStatus(ERROR);
                    } else {
                        const allImages = data?.result;

                        if (isMounted && Array.isArray(allImages)) {
                            allImages.forEach((item) => {
                                const {
                                    media_id,
                                    is_gallery: isGallery,
                                    is_primary: isHero,
                                    is_preview: isPreview,
                                    title,
                                    description,
                                    url,
                                    credits,
                                    position,
                                    created_date,
                                    updated_date,
                                } = item;

                                if (isGallery || (!isHero && !isPreview) && title && url) {
                                    galleryItems.push({
                                        media_id,
                                        altText: title,
                                        caption: description,
                                        url,
                                        credit: credits,
                                        position,
                                        created_date,
                                        updated_date,
                                    });
                                }

                                if (isHero && title && url) {
                                    heroItems.push({
                                        media_id,
                                        altText: title,
                                        caption: description,
                                        url,
                                        credit: credits,
                                        position,
                                        created_date,
                                        updated_date,
                                    });
                                }

                                if (isPreview && title && url) {
                                    previewItems.push({
                                        media_id,
                                        altText: title,
                                        caption: description,
                                        url,
                                        credit: credits,
                                        position,
                                        created_date,
                                        updated_date,
                                    });
                                }
                            });

                            // sort gallery by position and upload date
                            galleryItems.sort((a, b) => {
                                if (a.position < b.position) return -1;
                                if (b.position < a.position) return 1;
                                const keyA = Date.parse(a.created_date);
                                const keyB = Date.parse(b.created_date);
                                if (keyA < keyB) return -1;
                                if (keyA > keyB) return 1;
                                return 0;
                            });

                            setImagesObject({
                                galleryImages: galleryItems,
                                heroImage: heroItems[0],
                                previewImage: previewItems[0],
                            });
                            setStatus(SUCCESS);
                        }
                        // We'll assume a non-array return means there are no
                        // images for the given id ... return SUCCESS and empty
                        // sets for everything
                        else if (isMounted) {
                            setImagesObject({
                                galleryImages: [],
                                heroImage: {},
                                previewImage: {},
                            });
                            setStatus(SUCCESS);
                        }
                    }
                } catch (error) {
                    if (!axios.isCancel(error)) {
                        Sentry.captureException(error);
                    }
                }
            };

            loadData();
        }

        return () => {
            isMounted = false;
            cancelTokenSource.cancel();
        };
    }, [assetId, tourId, campsiteId]);

    return { ...imagesObject, status };
}
